import { useQuery } from '@tanstack/react-query';
import { pathOr, sortBy } from 'ramda';
import { useEffect, useState, useMemo } from 'react';

import { FormControl, FormErrorMessage, FormLabel } from '@frontend/components';

import Select from 'components/lib2/Select2';

import authService from 'services/authService';

import { useDebounce } from 'utils/hooks';

import Label from './Label';

const sortByName = sortBy((x) => x.name);

const UserGroupSelector = ({
  disabled,
  onChange,
  selectedUserGroup,
  optionsDisabledFn,
  iconToPutInFormLabelFn,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [options, setOptions] = useState([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 250);
  const { isLoading, isError, error, data } = useQuery(
    ['user-group-selector', debouncedSearchTerm, pageNumber],
    async () => {
      return await authService.getUserGroups({
        search: debouncedSearchTerm,
        page: pageNumber,
      });
    },
    {
      placeholderData: [],
    },
  );

  const dataOptions = useMemo(
    () =>
      sortByName(data.results ?? []).map((userGroup) => ({
        value: userGroup.id,
        label: <Label userGroup={userGroup} />,
        isDisabled: optionsDisabledFn?.(userGroup) ?? false,
      })),
    [data, optionsDisabledFn],
  );
  const resultsCount = data.count;

  // if the dataOptions are updated, add then next 20 or less to the current options array
  useEffect(() => {
    if (
      dataOptions &&
      !options.some(({ value }) => dataOptions[0]?.value == value) &&
      resultsCount > 0
    ) {
      setOptions(options.concat(dataOptions));
    }
  }, [dataOptions, resultsCount, options]);

  // when the search term is changed: go back to page 1, reset options to []
  useEffect(() => {
    setOptions([]);
    setPageNumber(1);
  }, [debouncedSearchTerm]);

  // when you scroll all the way to the bottom: the page number increases by 1 assuming there is more to load
  const handleOnScrollBottom = () => {
    if (options.length < resultsCount) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleChange = (id) => {
    onChange(options.find((userGroup) => userGroup.value === id));
  };

  return (
    <FormControl isInvalid={isError}>
      <FormLabel display="flex">
        Organization {iconToPutInFormLabelFn?.()}
      </FormLabel>
      <Select
        autoFocus
        disabled={disabled}
        filterOption={() => true} // react-select will automatically attempt to filter by label/value this allows for us to 'control' the filter to always return what options we pass it
        inputValue={searchTerm}
        isLoading={isLoading}
        onChange={handleChange}
        onInputChange={setSearchTerm}
        onMenuScrollToBottom={handleOnScrollBottom}
        options={options}
        placeholder="Select"
        value={selectedUserGroup ? selectedUserGroup.id : null}
      />
      <FormErrorMessage>
        Error: {pathOr('', ['name'], error)} - {pathOr('', ['message'], error)}
      </FormErrorMessage>
    </FormControl>
  );
};

export default UserGroupSelector;
